import {
    type PartnerCockpitNotification,
    type PartnerCockpitNotificationStatusFilter,
    PartnerCockpitNotificationStatusFilterValues,
} from '@houzy/schemas/partner-cockpit/notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useEffect, useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ListPagination } from '../../../components/ListPagination';
import { useTrackEvent } from '../../../lib/trackingEvents';
import {
    DEFAULT_NOTIFICATIONS_QUERY,
    useMarkAllNotificationsAsRead,
    useMarkNotificationAsRead,
    useNotifications,
    useUnreadNotificationsCount,
} from '../api/notificationsApi';
import { NotificationsList } from '../components/NotificationsList';
import { useAutoReadInformationalNotifications } from '../state/useAutoReadInformationalNotifications';

// Below this the tabs span the card and the mark-all action moves onto its own row above them.
const DESKTOP = 'md';

const STATUS_TABS = [
    { status: PartnerCockpitNotificationStatusFilterValues.all, labelKey: 'NOTIFICATIONS.TABS.ALL' },
    { status: PartnerCockpitNotificationStatusFilterValues.unread, labelKey: 'NOTIFICATIONS.TABS.UNREAD' },
    { status: PartnerCockpitNotificationStatusFilterValues.read, labelKey: 'NOTIFICATIONS.TABS.READ' },
] as const;

function UnreadCountPill({ count }: { count: number }): ReactElement {
    return (
        <Typography
            component="span"
            variant="caption"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: '999px',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
            }}>
            {count}
        </Typography>
    );
}

export function NotificationsPage(): ReactElement {
    const { t } = useTranslation();
    const headingId = useId();

    const [status, setStatus] = useState<PartnerCockpitNotificationStatusFilter>(DEFAULT_NOTIFICATIONS_QUERY.status);
    const [start, setStart] = useState(DEFAULT_NOTIFICATIONS_QUERY.start);
    const [pageSize, setPageSize] = useState(DEFAULT_NOTIFICATIONS_QUERY.pageSize);

    const query = useNotifications({ status, start, pageSize });
    const { data: unreadCount } = useUnreadNotificationsCount();
    const markAsRead = useMarkNotificationAsRead();
    const markAllAsRead = useMarkAllNotificationsAsRead();

    const total = query.data?.total ?? 0;
    const hasUnread = (unreadCount ?? 0) > 0;

    useAutoReadInformationalNotifications(query.data?.items);
    const notifications = query.data?.items ?? [];

    useEffect(() => {
        if (total > 0 && start >= total) {
            setStart((Math.ceil(total / pageSize) - 1) * pageSize);
        }
    }, [total, start, pageSize]);

    const changeStatus = (nextStatus: PartnerCockpitNotificationStatusFilter): void => {
        setStatus(nextStatus);
        setStart(0);
    };

    const track = useTrackEvent();

    const handleSelect = (notification: PartnerCockpitNotification): void => {
        track('Notification Clicked', { notificationType: notification.type });
        markAsRead.mutate(notification.uuid);
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, [DESKTOP]: 3 } }}>
            <Typography
                variant="h4"
                component="h1"
                id={headingId}
                sx={{ pt: 3, pb: { xs: 2, [DESKTOP]: 3 }, typography: { xs: 'h5', [DESKTOP]: 'h4' } }}>
                {t('NOTIFICATIONS.TITLE')}
            </Typography>

            <Card variant="outlined" component="section" aria-labelledby={headingId}>
                <Stack
                    // Reversed rather than reordered: the design stacks the action above the tabs, while the
                    // keyboard should still reach the tabs first, as it does on the desktop row.
                    direction={{ xs: 'column-reverse', [DESKTOP]: 'row' }}
                    alignItems={{ xs: 'stretch', [DESKTOP]: 'center' }}
                    justifyContent="space-between"
                    sx={{ px: 2, pt: { xs: 1, [DESKTOP]: 0 }, flexWrap: 'wrap', gap: 1 }}>
                    <Tabs
                        value={status}
                        onChange={(_event, nextStatus: PartnerCockpitNotificationStatusFilter) =>
                            changeStatus(nextStatus)
                        }
                        aria-label={t('NOTIFICATIONS.TABS.ARIA')}
                        sx={{ '& .MuiTab-root': { flex: { xs: '1 1 0', [DESKTOP]: '0 0 auto' }, minWidth: 0 } }}>
                        {STATUS_TABS.map((tab) => (
                            <Tab
                                key={tab.status}
                                value={tab.status}
                                label={
                                    tab.status === PartnerCockpitNotificationStatusFilterValues.unread && hasUnread ? (
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <span>{t(tab.labelKey)}</span>
                                            <UnreadCountPill count={unreadCount ?? 0} />
                                        </Stack>
                                    ) : (
                                        t(tab.labelKey)
                                    )
                                }
                            />
                        ))}
                    </Tabs>
                    <Button
                        size="small"
                        startIcon={<DoneAllIcon />}
                        sx={{ alignSelf: 'center' }}
                        disabled={!hasUnread || markAllAsRead.isPending}
                        onClick={() => markAllAsRead.mutate()}>
                        {t('NOTIFICATIONS.MARK_ALL_READ')}
                    </Button>
                </Stack>
                <Divider />

                {query.isError ? (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {t('ERRORS.GENERIC.TITLE')}
                    </Alert>
                ) : query.isPending ? (
                    <Stack spacing={1} sx={{ p: 2 }} aria-busy>
                        <Skeleton variant="rounded" height={72} />
                        <Skeleton variant="rounded" height={72} />
                        <Skeleton variant="rounded" height={72} />
                    </Stack>
                ) : notifications.length > 0 ? (
                    <NotificationsList
                        notifications={notifications}
                        onSelect={handleSelect}
                        onMarkAsRead={handleSelect}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 4, textAlign: 'center' }}>
                        {t('NOTIFICATIONS.EMPTY')}
                    </Typography>
                )}

                {total > 0 && (
                    <Box sx={{ p: 2 }}>
                        <ListPagination
                            page={Math.floor(start / pageSize)}
                            pageSize={pageSize}
                            total={total}
                            onPageChange={(page) => setStart(page * pageSize)}
                            onPageSizeChange={(nextPageSize) => {
                                setPageSize(nextPageSize);
                                setStart(0);
                            }}
                            itemsPerPageLabel={t('NOTIFICATIONS.ITEMS_PER_PAGE')}
                            paginationAriaLabel={t('NOTIFICATIONS.PAGINATION_ARIA')}
                        />
                    </Box>
                )}
            </Card>
        </Box>
    );
}
